export const VIDEO_EDITOR_FPS = 30;
// Preview cushion above Remotion's 0.65s default. Larger values avoid
// keyframe rewinds; the editor keeps the composition clock from lagging
// so this should rarely trigger.
export const VIDEO_EDITOR_PREVIEW_ACCEPTABLE_TIME_SHIFT_SECONDS = 1;

export const VIDEO_EDITOR_TRACK_TYPE = {
  VIDEO: "video",
  IMAGE: "image",
  AUDIO: "audio",
  SUBTITLE: "subtitle",
};

export const CLIP_PLACEMENT_OVERLAY = "overlay";
export const CLIP_PLACEMENT_SEQUENCE = "sequence";

export const VIDEO_EDITOR_TIMELINE_EFFECT = {
  VIDEO: "video",
  IMAGE: "image",
  AUDIO: "audio",
  SUBTITLE: "subtitle",
};

export const VIDEO_EDITOR_DEFAULT_COMPOSITION = {
  width: 1080,
  height: 1920,
};

export const VIDEO_EDITOR_MIN_CLIP_DURATION = 0.1;
export const VIDEO_EDITOR_MIN_PLAYBACK_RATE = 0.25;
export const VIDEO_EDITOR_MAX_PLAYBACK_RATE = 1;
export const VIDEO_EDITOR_HISTORY_LIMIT = 50;
export const VIDEO_EDITOR_TIMELINE_ZOOM_MIN = 0.5;
export const VIDEO_EDITOR_TIMELINE_ZOOM_MAX = 16;
export const VIDEO_EDITOR_TIMELINE_ZOOM_STEP = 1.15;

export const VIDEO_EDITOR_TRACK_ORDER = [
  VIDEO_EDITOR_TRACK_TYPE.IMAGE,
  VIDEO_EDITOR_TRACK_TYPE.SUBTITLE,
  VIDEO_EDITOR_TRACK_TYPE.VIDEO,
  VIDEO_EDITOR_TRACK_TYPE.AUDIO,
];

/** Mock design tokens (local to video editor modal). */
export const VIDEO_EDITOR_SHELL_BG = "#f7f8fa";
export const VIDEO_EDITOR_CANVAS_BG = "#f6f7f9";
export const VIDEO_EDITOR_WORKSPACE_PADDING_BOTTOM = 16;
export const VIDEO_EDITOR_TIMELINE_ROW_BG = "#eef0f4";
export const VIDEO_EDITOR_ACCENT = "#5c36ed";
export const VIDEO_EDITOR_ACCENT_DARK = "#5734e8";
export const VIDEO_EDITOR_PLAYHEAD = "#20252d";
export const VIDEO_EDITOR_LABEL_COLOR = "#667387";
export const VIDEO_EDITOR_CLIP_VIDEO_BORDER = "#6e4aff";
export const VIDEO_EDITOR_CLIP_VIDEO_HALO = "#e7e1ff";
export const VIDEO_EDITOR_CLIP_ACTION_BG = "#7045dc";

export const VIDEO_EDITOR_TIMELINE_ROW_HEIGHT_COMPACT = 29;
export const VIDEO_EDITOR_TIMELINE_ROW_HEIGHT_CONTENT = 61;
export const VIDEO_EDITOR_TIMELINE_ROW_GAP = 4;
export const VIDEO_EDITOR_TIMELINE_MAX_VISIBLE_ROWS = 4;
export const VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT = 30;
export const VIDEO_EDITOR_PLAYBACK_CONTROLS_HEIGHT = 40;
export const VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP = 12;
export const VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM = 14;
export const VIDEO_EDITOR_TIMELINE_RESIZE_HANDLE_HEIGHT = 10;
export const VIDEO_EDITOR_TIMELINE_MIN_PLAYER_HEIGHT = 160;
export const VIDEO_EDITOR_PREVIEW_BUSY_Z_INDEX = 20;
export const VIDEO_EDITOR_TIMELINE_ROW_HEIGHT =
  VIDEO_EDITOR_TIMELINE_ROW_HEIGHT_CONTENT;
export const VIDEO_EDITOR_TIMELINE_RAIL_WIDTH = 0;
export const VIDEO_EDITOR_TIMELINE_MIN_VISIBLE_SECONDS = 70;
export const VIDEO_EDITOR_TIMELINE_START_LEFT = 0;

export const getTimelineRowHeight = ({ type }) =>
  type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
    ? VIDEO_EDITOR_TIMELINE_ROW_HEIGHT_CONTENT
    : VIDEO_EDITOR_TIMELINE_ROW_HEIGHT_COMPACT;

export const getTimelineRowSlotHeight = ({ type, isLast = false }) => {
  const rowHeight = getTimelineRowHeight({ type });
  return isLast ? rowHeight : rowHeight + VIDEO_EDITOR_TIMELINE_ROW_GAP;
};

export const VIDEO_EDITOR_TIMELINE_ROWS_HEIGHT =
  VIDEO_EDITOR_TRACK_ORDER.reduce(
    (total, type, index) =>
      total +
      getTimelineRowSlotHeight({
        type,
        isLast: index === VIDEO_EDITOR_TRACK_ORDER.length - 1,
      }),
    0
  );

export const VIDEO_EDITOR_TIMELINE_EDITOR_HEIGHT =
  VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT + VIDEO_EDITOR_TIMELINE_ROWS_HEIGHT;

export const VIDEO_EDITOR_TIMELINE_HEIGHT =
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_TOP +
  VIDEO_EDITOR_TIMELINE_EDITOR_HEIGHT +
  VIDEO_EDITOR_TIMELINE_SECTION_PADDING_BOTTOM;

export const getTimelineRowTop = ({ type }) => {
  let top = VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;

  for (const trackType of VIDEO_EDITOR_TRACK_ORDER) {
    if (trackType === type) {
      return top;
    }

    top += getTimelineRowSlotHeight({ type: trackType });
  }

  return top;
};

export const getTimelineVisibleDuration = ({ durationSeconds }) =>
  Math.max(
    durationSeconds || VIDEO_EDITOR_TIMELINE_SCALE,
    VIDEO_EDITOR_TIMELINE_MIN_VISIBLE_SECONDS
  );

export const timeToTimelinePixels = ({
  time = 0,
  scaleWidth = VIDEO_EDITOR_TIMELINE_SCALE_WIDTH,
  scale = VIDEO_EDITOR_TIMELINE_SCALE,
  startLeft = VIDEO_EDITOR_TIMELINE_START_LEFT,
}) => startLeft + (time / scale) * scaleWidth;

export const formatVideoEditorClock = (timeInSeconds) => {
  const total = Math.max(0, Math.floor(Number(timeInSeconds) || 0));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/** Seconds shown on each major ruler group (visual emphasis every 10s). */
export const VIDEO_EDITOR_TIMELINE_MAJOR_SCALE = 10;
/** Timeline engine scale unit in seconds (label every 2s: 0, 2 … 10, 12, 14 …). */
export const VIDEO_EDITOR_TIMELINE_SCALE = 2;
/** Must be 1 so every 2s column gets a label (10, 12, 14, 16, 18, 20 …). */
export const VIDEO_EDITOR_TIMELINE_SCALE_SPLIT_COUNT = 1;
export const VIDEO_EDITOR_TIMELINE_SCALE_WIDTH = 80;
export const VIDEO_EDITOR_TIMELINE_FIT_PADDING = 48;
export const VIDEO_EDITOR_TIMELINE_SUBDIVISION_SECONDS = 2;

export const VIDEO_EDITOR_TIMELINE_SNAP_PIXELS = 8;
export const VIDEO_EDITOR_TIMELINE_LANE_HYSTERESIS_PX = 12;
export const VIDEO_EDITOR_TIMELINE_AUTO_SCROLL_EDGE = 36;
export const VIDEO_EDITOR_TIMELINE_KEYBOARD_NUDGE_SECONDS = 1 / VIDEO_EDITOR_FPS;
export const VIDEO_EDITOR_TIMELINE_KEYBOARD_NUDGE_LARGE_SECONDS = 1;

export const timelinePixelsToTime = ({
  pixels = 0,
  scaleWidth = VIDEO_EDITOR_TIMELINE_SCALE_WIDTH,
  scale = VIDEO_EDITOR_TIMELINE_SCALE,
  startLeft = VIDEO_EDITOR_TIMELINE_START_LEFT,
}) => Math.max(0, ((pixels - startLeft) / scaleWidth) * scale);

export const snapTimeToFrame = ({ time = 0, fps = VIDEO_EDITOR_FPS }) => {
  const frameDuration = 1 / Math.max(1, fps);
  return Math.max(0, Math.round(Number(time) / frameDuration) * frameDuration);
};

export const getTimelineFitScaleWidth = ({
  availableWidth,
  durationSeconds,
}) => {
  const visibleDuration = getTimelineVisibleDuration({ durationSeconds });
  const usableWidth = Math.max(
    availableWidth -
      VIDEO_EDITOR_TIMELINE_START_LEFT -
      VIDEO_EDITOR_TIMELINE_FIT_PADDING,
    160
  );

  return (usableWidth * VIDEO_EDITOR_TIMELINE_SCALE) / visibleDuration;
};

export const clampTimelineZoomLevel = (zoomLevel) =>
  Math.min(
    VIDEO_EDITOR_TIMELINE_ZOOM_MAX,
    Math.max(VIDEO_EDITOR_TIMELINE_ZOOM_MIN, Number(zoomLevel) || 1)
  );

export const getClipPlaybackRate = ({ clip }) => {
  const rate = Number(clip?.playbackRate);
  if (!Number.isFinite(rate) || rate <= 0) {
    return 1;
  }

  return Math.min(
    VIDEO_EDITOR_MAX_PLAYBACK_RATE,
    Math.max(VIDEO_EDITOR_MIN_PLAYBACK_RATE, rate)
  );
};

export const VIDEO_EDITOR_CLIP_COLORS = {
  [VIDEO_EDITOR_TRACK_TYPE.VIDEO]: VIDEO_EDITOR_CLIP_VIDEO_BORDER,
  [VIDEO_EDITOR_TRACK_TYPE.IMAGE]: "#d97706",
  [VIDEO_EDITOR_TRACK_TYPE.AUDIO]: "#16a34a",
  [VIDEO_EDITOR_TRACK_TYPE.SUBTITLE]: "#0ea5e9",
};
